import React, { PropsWithChildren, useCallback } from 'react';
import YouTube from 'react-youtube';

import classNames from 'classnames';
import Image from 'next/image';

import Flexbox from '@/components/common/Flexbox';
import { Article, GetArticleBodyIncludeTypeByType } from '@/constants/article';

import { Body } from './Body';

export const Heading: React.FC<
  PropsWithChildren<{
    level: 'h1' | 'h2' | 'h3' | 'h4';
  }>
> = ({ children, level }) => {
  return React.createElement(
    level,
    {
      className: classNames({
        'mt-2 md:mt-4 md:text-4xl font-bold whitespace-pre-line text-3xl': level === 'h1',
        'mt-10 md:mt-16 md:text-3xl font-bold whitespace-pre-line text-2xl': level === 'h2',
        'mt-8 md:mt-14 md:text-2xl font-bold whitespace-pre-line text-xl': level === 'h3',
        'mt-6 md:mt-12 md:text-xl font-bold whitespace-pre-line text-lg': level === 'h4',
      }),
    },
    children
  );
};

export const PostImage: React.FC<{
  content: Array<{
    src: string;
    alt: string;
  }>;
}> = ({ content }) => {
  return (
    <div className={classNames('relative mt-6 mb-2 w-full md:mb-4 md:mt-8')}>
      {content.map(
        ({
          src,
          alt = '',
          orientation = 'horizontal',
        }: GetArticleBodyIncludeTypeByType<'image'>['content'][number]) => (
          <Flexbox align={'center'} justify={'center'} direction={'column'} key={alt} className={'w-full space-y-2'}>
            <div className={classNames('relative w-full max-w-[42.5rem]')}>
              <div className={classNames(orientation === 'vertical' ? 'pt-[calc(100%/2*3)]' : 'pt-[calc(100%/4*3)]')} />
              <div className={'absolute top-0 left-0 h-full w-full'}>
                <Image src={src} alt={alt} layout={'fill'} className={'object-cover'} />
              </div>
            </div>
            <p className={classNames('text-xs text-gray500 md:text-sm', 'whitespace-pre-line')}>{alt}</p>
          </Flexbox>
        )
      )}
    </div>
  );
};

export const PostVideo: React.FC<{
  content: Array<{
    src: 'youtube';
    id: string;
  }>;
}> = ({ content }) => {
  return (
    <div className={classNames('relative mt-6 mb-2 w-full md:mb-4 md:mt-8')}>
      {content.map(({ id = '' }) => (
        <YTVideo id={id} key={id} />
      ))}
    </div>
  );
};

const YTVideo: React.FC<{ id: string }> = ({ id }) => {
  return (
    <div className={'relative w-full'}>
      <div className={'w-full pt-[calc(100%*2/3)]'} />
      <div className={'absolute top-0 left-0 w-full h-full bg-gray50'}>
        <YouTube className={'w-full h-full'} videoId={id} containerClassName={'w-full h-full'} />
      </div>
    </div>
  );
};

export const Divider = () => {
  return <div className={classNames('mt-10 h-px w-full bg-gray500 md:mt-16')} />;
};

export const Hyperlink: React.FC<{
  content: Array<{
    title: string;
    href: string;
  }>;
}> = ({ content }) => {
  return (
    <Flexbox
      direction={'column'}
      className={classNames('mt-4 md:mt-8', 'text-sm2 text-gold md:text-base', 'underline')}
    >
      {content.map(({ title, href }, index) => (
        <a key={`${title}_${index}`} target={'_blank'} rel={'noreferrer noopener'} href={href}>
          {title}
        </a>
      ))}
    </Flexbox>
  );
};

export const PostList: React.FC<{
  type: 'list-decimal' | 'list-dot';
  content: Array<string>;
}> = ({ type, content }) => {
  return (
    <ul className={classNames('mt-4 md:mt-8', 'text-xs text-gray800 md:text-sm')}>
      {content.map((item, index) => (
        <Flexbox
          as={'li'}
          key={`${item}_${index}`}
          className={classNames('mt-4', 'list-inside', 'list-none', 'text-xs md:text-sm')}
        >
          <Flexbox align={'center'} justify={'center'} className={'mr-2 h-[18px] md:h-[21px]'}>
            {type === 'list-dot' && <span className={'h-1 w-1 rounded-full bg-gray800'} />}
            {type === 'list-decimal' && <span>{index + 1}.</span>}
          </Flexbox>
          <p>{item}</p>
        </Flexbox>
      ))}
    </ul>
  );
};

export const Table: React.FC<GetArticleBodyIncludeTypeByType<'table'>> = ({ content, columnConfig }) => {
  const getColumnWidth = useCallback(
    (index: number) => {
      const width = columnConfig?.[index]?.width;
      if (width) return `${width}px`;
      return undefined;
    },
    [columnConfig]
  );

  return (
    <div className={'max-w-full overflow-scroll mt-6'}>
      <div className={classNames('w-max overflow-x-scroll', 'border border-gray500 rounded-2xl')}>
        <table className={'w-max rounded-2xl divide-y divide-gray700'}>
          <thead>
            <tr className={'bg-gray900 text-white divide-x divide-gray700'}>
              {content.header.map((item, index) => (
                <th
                  key={`${item}_${index}`}
                  className={classNames('py-4 px-6', 'text-sm text-gray-2 md:text-base', 'w-fit')}
                  style={{
                    width: getColumnWidth(index),
                  }}
                >
                  {item}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={'divide-y divide-gray700'}>
            {content.body.map(({ row }, index) => (
              <tr key={index} className={'divide-x divide-gray700'}>
                {content.header.map((_, index) => {
                  const item = row[index];
                  return (
                    <td
                      key={`${item}_${index}`}
                      className={classNames('py-4 px-6', 'text-sm text-gray-2 md:text-base', 'align-middle', 'w-fit')}
                      style={{
                        width: getColumnWidth(index),
                      }}
                    >
                      {item}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { Body };

export const ArticleComponentDispatcher: React.FC<{
  body: Article['body'][number];
}> = ({ body }) => {
  switch (body.type) {
    case 'body':
      return <Body hypertext={body.hypertext} content={body.content} highlight={body.highlight} />;
    case 'image':
      return <PostImage content={body.content} />;
    case 'video':
      return <PostVideo content={body.content} />;
    case 'divider':
      return <Divider />;
    case 'hyperlink':
      return <Hyperlink content={body.content} />;
    case 'list-dot':
    case 'list-decimal':
      return <PostList type={body.type} content={body.content} />;
    case 'table':
      return <Table type={body.type} content={body.content} />;
    default:
      return <Heading level={body.type}>{body.content.join('\n')}</Heading>;
  }
};
